// ===========================================
// Firestore Collection Paths for Referral CRM PWA
// ===========================================
// All collections are namespaced under 'referral-crm' root document
// to separate from other services in the same Firebase project.

import {
  collection,
  doc,
  CollectionReference,
  DocumentReference,
  Firestore,
} from 'firebase/firestore';

/**
 * Root document path for all referral-crm data
 */
export const ROOT = 'referral-crm';

/**
 * Collection names (relative to root)
 */
export const COLLECTIONS = {
  // Core data
  USERS: 'users',
  COMPANIES: 'companies',
  PROJECTS: 'projects',

  // Configuration
  CONFIG: 'config',
  COMMISSION_RULES: 'commissionRules',

  // Partner portal
  PARTNER_TOKENS: 'partnerTokens',
  PARTNER_SESSIONS: 'partnerSessions',
  PROJECT_UPDATES: 'projectUpdates',

  // Audit & sync
  AUDIT_LOGS: 'auditLogs',
  SYNC_STATUS: 'syncStatus',

  // Evidence
  EVIDENCE: 'evidence',

  // Notifications (Phase 8)
  NOTIFICATIONS: 'notifications',
} as const;

/**
 * Config document IDs
 */
export const CONFIG_DOCS = {
  APP_CONFIG: 'appConfig',
} as const;

/**
 * Get the root data document reference
 * All collections are nested under: referral-crm/data/
 */
export function getDataRoot(db: Firestore): DocumentReference {
  return doc(db, ROOT, 'data');
}

/**
 * Get a namespaced collection reference
 * @param db - Firestore instance
 * @param collectionName - Collection name from COLLECTIONS
 * @returns Collection reference under referral-crm/data/{collection}
 */
export function getCollection(db: Firestore, collectionName: string): CollectionReference {
  return collection(getDataRoot(db), collectionName);
}

// ===========================================
// Typed Collection Accessors
// ===========================================

export function usersCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.USERS);
}

export function companiesCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.COMPANIES);
}

export function projectsCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.PROJECTS);
}

export function configCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.CONFIG);
}

export function commissionRulesCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.COMMISSION_RULES);
}

export function partnerTokensCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.PARTNER_TOKENS);
}

export function partnerSessionsCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.PARTNER_SESSIONS);
}

export function projectUpdatesCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.PROJECT_UPDATES);
}

export function auditLogsCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.AUDIT_LOGS);
}

export function syncStatusCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.SYNC_STATUS);
}

export function evidenceCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.EVIDENCE);
}

export function notificationsCollection(db: Firestore): CollectionReference {
  return getCollection(db, COLLECTIONS.NOTIFICATIONS);
}

// ===========================================
// Config Document Accessors
// ===========================================

/**
 * Get a user document reference by user ID
 * Path: referral-crm/data/users/{userId}
 */
export function userDoc(db: Firestore, userId: string): DocumentReference {
  return doc(usersCollection(db), userId);
}

export function appConfigDoc(db: Firestore): DocumentReference {
  return doc(configCollection(db), CONFIG_DOCS.APP_CONFIG);
}

// ===========================================
// Subcollection Accessors
// ===========================================

/**
 * Get contacts subcollection for a company
 * Path: referral-crm/data/companies/{companyId}/contacts
 */
export function companyContactsCollection(
  db: Firestore,
  companyId: string
): CollectionReference {
  return collection(doc(companiesCollection(db), companyId), 'contacts');
}

/**
 * Storage paths for referral-crm
 */
export const STORAGE_PATHS = {
  EVIDENCE: 'referral-crm/evidence',
  PROFILES: 'referral-crm/profiles',
} as const;

/**
 * Get storage path for evidence
 * @param tenantId - Tenant identifier (usually 'default')
 * @param business - Business name (azdisaster/blacksmith)
 * @param projectId - Project ID
 * @param filename - File name
 */
export function getEvidenceStoragePath(
  tenantId: string,
  business: string,
  projectId: string,
  filename: string
): string {
  return `${STORAGE_PATHS.EVIDENCE}/${tenantId}/${business}/${projectId}/${filename}`;
}

/**
 * Get storage path for user profile photos
 * @param userId - User ID
 * @param filename - File name
 */
export function getProfileStoragePath(userId: string, filename: string): string {
  return `${STORAGE_PATHS.PROFILES}/${userId}/${filename}`;
}
